/**
 * HTTP client for the general-repricing Setup API (webback blueprint
 * `repricing`, `/api/repricing/setup/*`). Same style as the My Business
 * admin pages: same-origin `fetch` with cookies, JSON in and out.
 *
 * The transport itself lives in `DsRepricing/@services/http` — the
 * Strategies wizard uses the same client (CU-869f043c1).
 */
import { BACKEND_APIS } from '@constants/routes'
import { makeClient, resource as makeResource } from 'Pages/DsRepricing/@services/http'

export const API = BACKEND_APIS.GENERAL_REPRICING_SETUP_API

export { ApiError } from 'Pages/DsRepricing/@services/http'

const http = makeClient(API)

export const setupApi = {
	consistencyGroups: makeResource(http, '/consistency-groups'),
	priceLimits:       makeResource(http, '/price-limits'),
	priceMatrices:     makeResource(http, '/price-matrices'),
	//: How many scopes a matrix prices. Editing one moves them all at
	//: once, so the editor says how many before it saves.
	matrixUsage:       (id) => http.get(`/price-matrices/${id}/usage`).then(r => r.scopes),
	//: Simulation or live, plus the pricelists live would write to.
	//: Quarantine thresholds: how far a price may move before a human looks.
	quarantine:        { get: () => http.get('/quarantine'),
	                     set: (values, reason) => http.put('/quarantine', { ...values, reason }) },
	mode:              { get: () => http.get('/mode'),
	                     set: (mode, reason) => http.put('/mode', { mode, reason }) },
	faces:             makeResource(http, '/rate-cards/faces'),
	tethers:           makeResource(http, '/tethers'),
	pricelists:        () => http.get('/pricelists').then(r => r.items),
	// The tenant's own brands, for the franchise axis. Tenant
	// configuration, not the provider catalogue — see the webback
	// `own_providers` service.
	ownProviders:      () => http.get('/own-providers').then(r => r.items),
	// Every vehicle category the PMS knows, mapped to a market ACRISS
	// or not. Replaces the hand-written `@mocks/catalog` tree, which
	// missed eight codes the engine reprices and offered twenty that
	// do not exist in the fleet (2026-09-28).
	categories:        () => http.get('/categories').then(r => r.items),
	inheritedHint:     (code) => http.get(`/rate-cards/inherited-hint/${encodeURIComponent(code)}`),
	// A proposal, not a write: the server answers with an order plus the
	// evidence behind each position and applies nothing. See the
	// CategoryConsistency SuggestOrder dialog.
	suggestConsistencyOrder: (values, days) =>
		http.post('/consistency-groups/suggest-order', days ? { values, days } : { values }),
	faceTiers:         (id) => http.get(`/rate-cards/faces/${id}/tiers`),
	saveFaceTiers:     (id, tiers, reason) => http.put(`/rate-cards/faces/${id}/tiers`, { tiers, reason }),
}
